/**
 * Opal - Full QA & Monitoring AI Dashboard
 * Phase 3: Complete system health, flow checks, friends integrity, regressions, recurring issues
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '../AdminApp';
import { toast } from 'sonner';
import {
  Activity, AlertTriangle, CheckCircle, XCircle, RefreshCw, Clock,
  Database, Shield, Wifi, Eye, ChevronDown, ChevronUp, Bell, BellOff,
  Users, GitBranch, Repeat, Zap, ArrowRight
} from 'lucide-react';

const SEVERITY_COLORS = {
  P0: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-800' },
  P1: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', badge: 'bg-orange-100 text-orange-800' },
  P2: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', badge: 'bg-yellow-100 text-yellow-800' },
};

const STATUS_CONFIG = {
  healthy: { icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'All Systems Operational' },
  degraded: { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50', label: 'Degraded Performance' },
  critical: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', label: 'Critical Issues Detected' },
  unknown: { icon: Activity, color: 'text-gray-500', bg: 'bg-gray-50', label: 'Status Unknown' },
};

const TABS = [
  { id: 'qa', label: 'QA Dashboard', icon: Eye },
  { id: 'health', label: 'System Health', icon: Activity },
  { id: 'flows', label: 'Flow Check', icon: Zap },
  { id: 'friends', label: 'Friends Integrity', icon: Users },
  { id: 'parity', label: 'Parity Check', icon: GitBranch },
  { id: 'regressions', label: 'Regressions', icon: ArrowRight },
  { id: 'recurring', label: 'Recurring Issues', icon: Repeat },
  { id: 'trends', label: 'Trends', icon: Activity },
  { id: 'alerts', label: 'Alerts', icon: Bell },
  { id: 'proposals', label: 'Fix Proposals', icon: GitBranch },
];

const OpalPage = () => {
  const { api } = useAdmin();
  const [healthData, setHealthData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkRunning, setCheckRunning] = useState(false);
  const [expandedSections, setExpandedSections] = useState({ endpoints: true, auth: true, analysis: true });
  const [activeTab, setActiveTab] = useState('qa');
  const [parityData, setParityData] = useState(null);
  const [trendsData, setTrendsData] = useState(null);
  const [flowData, setFlowData] = useState(null);
  const [friendsData, setFriendsData] = useState(null);
  const [regressionsData, setRegressionsData] = useState(null);
  const [recurringData, setRecurringData] = useState(null);
  const [tabLoading, setTabLoading] = useState({});
  
  // New QA Dashboard state
  const [qaDashboard, setQaDashboard] = useState(null);
  const [qaRunning, setQaRunning] = useState(false);
  const [fixProposals, setFixProposals] = useState([]);

  const fetchHealth = useCallback(async () => {
    try {
      setCheckRunning(true);
      const response = await api.get('/api/admin/opal/health');
      setHealthData(response.data);
    } catch (error) {
      toast.error('Failed to fetch system health');
    } finally {
      setCheckRunning(false);
      setLoading(false);
    }
  }, [api]);

  const fetchAlerts = useCallback(async () => {
    try {
      const response = await api.get('/api/admin/opal/alerts');
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Failed to fetch alerts');
    }
  }, [api]);

  const fetchQaDashboard = useCallback(async () => {
    try {
      const response = await api.get('/api/admin/opal/qa/dashboard');
      setQaDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch QA dashboard');
    }
  }, [api]);

  const fetchFixProposals = useCallback(async () => {
    try {
      const response = await api.get('/api/admin/opal/qa/proposals');
      setFixProposals(response.data.proposals || []);
    } catch (error) {
      console.error('Failed to fetch fix proposals');
    }
  }, [api]);

  useEffect(() => { fetchHealth(); fetchAlerts(); fetchQaDashboard(); fetchFixProposals(); }, [fetchHealth, fetchAlerts, fetchQaDashboard, fetchFixProposals]);

  const dismissAlert = async (alertId) => {
    try {
      await api.post('/api/admin/opal/dismiss-alert', { alert_id: alertId });
      setAlerts(prev => prev.filter(a => a.id !== alertId));
      toast.success('Alert dismissed');
    } catch (error) {
      toast.error('Failed to dismiss alert');
    }
  };

  const loadTab = async (tabId) => {
    const loaders = {
      parity: { state: parityData, setter: setParityData, url: '/api/admin/opal/parity' },
      trends: { state: trendsData, setter: setTrendsData, url: '/api/admin/opal/trends' },
      flows: { state: flowData, setter: setFlowData, url: '/api/admin/opal/flow-check' },
      friends: { state: friendsData, setter: setFriendsData, url: '/api/admin/opal/friends-integrity' },
      regressions: { state: regressionsData, setter: setRegressionsData, url: '/api/admin/opal/regressions' },
      recurring: { state: recurringData, setter: setRecurringData, url: '/api/admin/opal/recurring-issues' },
      proposals: { state: fixProposals.length > 0 ? fixProposals : null, setter: setFixProposals, url: '/api/admin/opal/qa/proposals', transform: (data) => data.proposals || [] },
    };
    const loader = loaders[tabId];
    if (!loader || loader.state) return;
    try {
      setTabLoading(prev => ({ ...prev, [tabId]: true }));
      const response = await api.get(loader.url);
      if (loader.transform) {
        loader.setter(loader.transform(response.data));
      } else {
        loader.setter(response.data);
      }
    } catch (error) {
      toast.error(`Failed to load ${tabId}`);
    } finally {
      setTabLoading(prev => ({ ...prev, [tabId]: false }));
    }
  };

  const runQaSuite = async () => {
    try {
      setQaRunning(true);
      toast.info('Running QA test suite...');
      const response = await api.post('/api/admin/opal/qa/run');
      setQaDashboard(prev => ({ ...prev, latest_run: response.data, qa_score: response.data.qa_score }));
      toast.success(`QA run complete! Score: ${response.data.qa_score}`);
      fetchQaDashboard();
      fetchAlerts();
      fetchFixProposals();
    } catch (error) {
      toast.error('QA run failed');
    } finally {
      setQaRunning(false);
    }
  };

  const updateProposalStatus = async (proposalId, status) => {
    try {
      await api.post(`/api/admin/opal/qa/proposals/${proposalId}/status`, null, { params: { status } });
      toast.success(`Proposal ${status}`);
      fetchFixProposals();
    } catch (error) {
      toast.error('Failed to update proposal');
    }
  };

  const runFullQA = async () => {
    try {
      setCheckRunning(true);
      toast.info('Running full QA suite...');
      const response = await api.get('/api/admin/opal/full-qa');
      const data = response.data;
      setHealthData(data);
      setFlowData(data.flow_check);
      setFriendsData(data.friends_integrity);
      setRegressionsData(data.regressions);
      setRecurringData(data.recurring_issues);
      setParityData(data.parity);
      setTrendsData(data.trends);
      fetchAlerts();
      toast.success('Full QA complete');
    } catch (error) {
      toast.error('Full QA failed');
    } finally {
      setCheckRunning(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="opal-loading">
        <div className="flex items-center gap-3 text-gray-500">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Opal is analyzing system health...</span>
        </div>
      </div>
    );
  }

  const status = healthData?.overall_status || 'unknown';
  const StatusIcon = STATUS_CONFIG[status]?.icon || Activity;

  return (
    <div className="space-y-6" data-testid="opal-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
            <Eye className="w-5 h-5 text-violet-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="opal-title">Opal &mdash; QA &amp; Monitoring</h1>
            <p className="text-sm text-gray-500">Full-stack QA engineer for GEMS Live</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={runQaSuite}
            disabled={qaRunning}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
            data-testid="opal-run-qa-btn"
          >
            <Eye className={`w-4 h-4 ${qaRunning ? 'animate-spin' : ''}`} />
            {qaRunning ? 'Running QA...' : 'Run QA Suite'}
          </button>
          <button
            onClick={fetchHealth}
            disabled={checkRunning}
            className="flex items-center gap-2 px-4 py-2 border border-violet-300 text-violet-700 rounded-lg hover:bg-violet-50 disabled:opacity-50 transition-colors"
            data-testid="opal-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${checkRunning ? 'animate-spin' : ''}`} />
            Quick Check
          </button>
          <button
            onClick={runFullQA}
            disabled={checkRunning}
            className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 transition-colors"
            data-testid="opal-full-qa-btn"
          >
            <Zap className={`w-4 h-4 ${checkRunning ? 'animate-spin' : ''}`} />
            {checkRunning ? 'Running...' : 'Run Full QA'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg overflow-x-auto">
        {TABS.map(tab => {
          const Icon = tab.icon;
          const alertCount = tab.id === 'alerts' ? alerts.length : 0;
          return (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); loadTab(tab.id); }}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid={`opal-tab-${tab.id}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {tab.label}{alertCount > 0 ? ` (${alertCount})` : ''}
            </button>
          );
        })}
      </div>

      {/* ===== QA DASHBOARD TAB ===== */}
      {activeTab === 'qa' && (
        <div className="space-y-6" data-testid="opal-qa-dashboard">
          {/* QA Score Banner */}
          <div className={`rounded-xl p-6 ${
            (qaDashboard?.qa_score || 0) >= 90 ? 'bg-emerald-50 border border-emerald-200' :
            (qaDashboard?.qa_score || 0) >= 70 ? 'bg-amber-50 border border-amber-200' :
            'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold ${
                  (qaDashboard?.qa_score || 0) >= 90 ? 'bg-emerald-100 text-emerald-700' :
                  (qaDashboard?.qa_score || 0) >= 70 ? 'bg-amber-100 text-amber-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {qaDashboard?.qa_score || 0}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">QA Score</h2>
                  <p className="text-sm text-gray-600">
                    {qaDashboard?.overall_status === 'healthy' ? 'All systems operational' :
                     qaDashboard?.overall_status === 'stable' ? 'Minor issues detected' :
                     qaDashboard?.overall_status === 'critical' ? 'Critical failures detected' : 'Status unknown'}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Last run</p>
                <p className="text-sm font-medium text-gray-700">
                  {qaDashboard?.summary?.last_run ? new Date(qaDashboard.summary.last_run).toLocaleString() : 'Never'}
                </p>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-white border rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-gray-900">{qaDashboard?.summary?.total_tests || 0}</p>
              <p className="text-sm text-gray-500">Total Tests</p>
            </div>
            <div className="bg-white border rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-emerald-600">{qaDashboard?.summary?.passed || 0}</p>
              <p className="text-sm text-gray-500">Passed</p>
            </div>
            <div className="bg-white border rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-red-600">{qaDashboard?.summary?.failed || 0}</p>
              <p className="text-sm text-gray-500">Failed</p>
            </div>
            <div className="bg-white border rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-red-700">{qaDashboard?.summary?.p0_failures || 0}</p>
              <p className="text-sm text-gray-500">P0 Failures</p>
            </div>
            <div className="bg-white border rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-orange-600">{qaDashboard?.summary?.p1_failures || 0}</p>
              <p className="text-sm text-gray-500">P1 Failures</p>
            </div>
          </div>

          {/* Critical Flows */}
          <div className="bg-white border rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Critical User Flows</h3>
              <span className="text-xs bg-violet-100 text-violet-700 px-2 py-1 rounded-full">
                {Object.keys(qaDashboard?.critical_flows || {}).length} flows monitored
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 divide-y md:divide-y-0 md:divide-x">
              {Object.entries(qaDashboard?.critical_flows || {}).map(([key, flow]) => {
                const lastResult = qaDashboard?.latest_run?.test_results?.find(r => r.flow_key === key);
                const passed = lastResult?.status === 'passed';
                return (
                  <div key={key} className="p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900">{flow.name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        flow.severity === 'P0' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                      }`}>{flow.severity}</span>
                    </div>
                    <p className="text-xs text-gray-500 mb-2">{flow.description}</p>
                    <div className="flex items-center gap-2">
                      {lastResult ? (
                        passed ? 
                          <><CheckCircle className="w-4 h-4 text-emerald-500" /><span className="text-xs text-emerald-600">Passed</span></> :
                          <><XCircle className="w-4 h-4 text-red-500" /><span className="text-xs text-red-600">Failed</span></>
                      ) : (
                        <><Clock className="w-4 h-4 text-gray-400" /><span className="text-xs text-gray-500">Not tested</span></>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Recent Alerts */}
          {qaDashboard?.recent_alerts?.length > 0 && (
            <div className="bg-white border rounded-xl overflow-hidden">
              <div className="px-5 py-4 border-b">
                <h3 className="font-semibold text-gray-900">Recent Alerts</h3>
              </div>
              <div className="divide-y">
                {qaDashboard.recent_alerts.slice(0, 5).map(alert => {
                  const sev = SEVERITY_COLORS[alert.severity] || SEVERITY_COLORS.P2;
                  return (
                    <div key={alert.id} className={`px-5 py-3 ${sev.bg}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${sev.badge}`}>{alert.severity}</span>
                          <span className="font-medium text-gray-900">{alert.title}</span>
                        </div>
                        <span className="text-xs text-gray-500">{new Date(alert.timestamp).toLocaleString()}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Run History */}
          {qaDashboard?.run_history?.length > 0 && (
            <div className="bg-white border rounded-xl overflow-hidden">
              <div className="px-5 py-4 border-b">
                <h3 className="font-semibold text-gray-900">Run History</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-gray-600 font-medium">Time</th>
                      <th className="px-4 py-3 text-center text-gray-600 font-medium">Score</th>
                      <th className="px-4 py-3 text-center text-gray-600 font-medium">Passed</th>
                      <th className="px-4 py-3 text-center text-gray-600 font-medium">Failed</th>
                      <th className="px-4 py-3 text-center text-gray-600 font-medium">P0</th>
                      <th className="px-4 py-3 text-center text-gray-600 font-medium">Triggered By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {qaDashboard.run_history.map((run, idx) => (
                      <tr key={run.run_id || idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-700">{new Date(run.completed_at || run.started_at).toLocaleString()}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`font-bold ${run.qa_score >= 90 ? 'text-emerald-600' : run.qa_score >= 70 ? 'text-amber-600' : 'text-red-600'}`}>
                            {run.qa_score}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center text-emerald-600">{run.passed}</td>
                        <td className="px-4 py-3 text-center text-red-600">{run.failed}</td>
                        <td className="px-4 py-3 text-center text-red-700">{run.p0_failures?.length || 0}</td>
                        <td className="px-4 py-3 text-center text-gray-500 capitalize">{run.triggered_by}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== FIX PROPOSALS TAB ===== */}
      {activeTab === 'proposals' && (
        <div className="space-y-4" data-testid="opal-proposals">
          {tabLoading.proposals ? (
            <div className="flex items-center justify-center h-32 text-gray-500">
              <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading fix proposals...
            </div>
          ) : fixProposals.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white border rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-violet-600">{fixProposals.length}</p>
                  <p className="text-sm text-gray-500">Total Proposals</p>
                </div>
                <div className="bg-white border rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-amber-600">{fixProposals.filter(p => p.status === 'proposed').length}</p>
                  <p className="text-sm text-gray-500">Pending Review</p>
                </div>
                <div className="bg-white border rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-emerald-600">{fixProposals.filter(p => p.status === 'implemented').length}</p>
                  <p className="text-sm text-gray-500">Implemented</p>
                </div>
              </div>
              {fixProposals.map(proposal => {
                const sev = SEVERITY_COLORS[proposal.severity] || SEVERITY_COLORS.P2;
                return (
                  <div key={proposal.proposal_id} className="bg-white border rounded-xl overflow-hidden">
                    <div className={`px-5 py-4 ${sev.bg} border-b`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${sev.badge}`}>{proposal.severity}</span>
                          <span className="font-semibold text-gray-900">{proposal.pr_title}</span>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          proposal.status === 'proposed' ? 'bg-amber-100 text-amber-700' :
                          proposal.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                          proposal.status === 'implemented' ? 'bg-emerald-100 text-emerald-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>{proposal.status}</span>
                      </div>
                    </div>
                    <div className="px-5 py-4 space-y-3">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Root Cause</p>
                        <p className="text-sm text-gray-600">{proposal.root_cause}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Suggested Fix</p>
                        <p className="text-sm text-gray-600">{proposal.suggested_fix}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Files to Modify</p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {proposal.code_changes?.map((change, idx) => (
                            <span key={idx} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                              {change.file}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">PR Description</p>
                        <pre className="text-xs bg-gray-50 p-3 rounded-lg overflow-x-auto whitespace-pre-wrap mt-1">{proposal.pr_description}</pre>
                      </div>
                      {proposal.status === 'proposed' && (
                        <div className="flex gap-2 pt-2">
                          <button
                            onClick={() => updateProposalStatus(proposal.proposal_id, 'approved')}
                            className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => updateProposalStatus(proposal.proposal_id, 'rejected')}
                            className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </>
          ) : (
            <div className="bg-white border rounded-xl p-8 text-center">
              <GitBranch className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No fix proposals yet. Run QA suite to detect issues and generate proposals.</p>
            </div>
          )}
        </div>
      )}

      {/* ===== HEALTH TAB ===== */}
      {activeTab === 'health' && healthData && (
        <>
          <div className={`${STATUS_CONFIG[status]?.bg} border rounded-xl p-5`} data-testid="opal-status-banner">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <StatusIcon className={`w-6 h-6 ${STATUS_CONFIG[status]?.color}`} />
                <div>
                  <h2 className={`text-lg font-semibold ${STATUS_CONFIG[status]?.color}`}>{STATUS_CONFIG[status]?.label}</h2>
                  <p className="text-sm text-gray-600 mt-1">{healthData.ai_summary}</p>
                </div>
              </div>
              <div className="text-right text-sm text-gray-500">
                <Clock className="w-4 h-4 inline mr-1" />
                {new Date(healthData.timestamp).toLocaleString()}
              </div>
            </div>
          </div>

          {healthData.ai_analysis?.issues?.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="opal-ai-analysis">
              <button onClick={() => toggleSection('analysis')} className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50">
                <div className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-violet-600" />
                  <span className="font-semibold text-gray-900">Opal&apos;s Analysis</span>
                  <span className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full">AI</span>
                </div>
                {expandedSections.analysis ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {expandedSections.analysis && (
                <div className="px-5 pb-5 space-y-3">
                  {healthData.ai_analysis.issues.map((issue, idx) => {
                    const sev = SEVERITY_COLORS[issue.severity] || SEVERITY_COLORS.P2;
                    return (
                      <div key={idx} className={`${sev.bg} ${sev.border} border rounded-lg p-4`}>
                        <div className="flex items-start justify-between mb-2">
                          <span className="font-medium text-gray-900">{issue.what}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${sev.badge}`}>{issue.severity}</span>
                        </div>
                        <div className="space-y-1 text-sm">
                          <p className={sev.text}><span className="font-medium">Where:</span> {issue.where}</p>
                          <p className="text-gray-600"><span className="font-medium">Likely cause:</span> {issue.likely_cause}</p>
                          <p className="text-gray-800"><span className="font-medium">Action:</span> {issue.recommended_action}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="opal-endpoints">
            <button onClick={() => toggleSection('endpoints')} className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50">
              <div className="flex items-center gap-2">
                <Wifi className="w-5 h-5 text-blue-600" />
                <span className="font-semibold text-gray-900">Endpoint Status</span>
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{healthData.endpoints?.length || 0} endpoints</span>
              </div>
              {expandedSections.endpoints ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {expandedSections.endpoints && (
              <div className="px-5 pb-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(healthData.endpoints || []).map((ep, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        {ep.status === 'healthy' ? <CheckCircle className="w-4 h-4 text-emerald-500" /> :
                         ep.status === 'degraded' ? <AlertTriangle className="w-4 h-4 text-amber-500" /> :
                         <XCircle className="w-4 h-4 text-red-500" />}
                        <span className="text-sm font-medium text-gray-800">{ep.name}</span>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        ep.status === 'healthy' ? 'bg-emerald-100 text-emerald-700' :
                        ep.status === 'degraded' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                      }`}>{ep.response_code}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="opal-database">
              <div className="flex items-center gap-2 mb-3">
                <Database className="w-5 h-5 text-blue-600" />
                <span className="font-semibold text-gray-900">Database</span>
              </div>
              <div className="flex items-center gap-2 mb-3">
                {healthData.database?.status === 'healthy' ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
                <span className="text-sm text-gray-700 capitalize">{healthData.database?.status}</span>
              </div>
              {healthData.database?.collections && (
                <div className="space-y-1">
                  {Object.entries(healthData.database.collections).map(([name, count]) => (
                    <div key={name} className="flex justify-between text-sm">
                      <span className="text-gray-500">{name}</span>
                      <span className="font-medium text-gray-700">{count.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="opal-auth">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-5 h-5 text-violet-600" />
                <span className="font-semibold text-gray-900">Auth Flows</span>
              </div>
              {(!healthData.auth_issues || healthData.auth_issues.length === 0) ? (
                <div className="flex items-center gap-2 text-emerald-600">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm">All authentication flows operational</span>
                </div>
              ) : (
                <div className="space-y-2">
                  {healthData.auth_issues.map((issue, idx) => (
                    <div key={idx} className="p-2 bg-red-50 rounded-lg border border-red-100">
                      <span className="text-xs font-medium text-red-700">[{issue.severity}] {issue.component}</span>
                      <p className="text-xs text-red-600 mt-1">{issue.error}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ===== FLOW CHECK TAB ===== */}
      {activeTab === 'flows' && (
        <div className="space-y-4" data-testid="opal-flows">
          {tabLoading.flows ? (
            <div className="flex items-center justify-center h-32 text-gray-500"><RefreshCw className="w-5 h-5 animate-spin mr-2" /> Testing critical flows...</div>
          ) : flowData ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white border rounded-xl p-4 text-center">
                  <p className="text-sm text-gray-500">Total Flows</p>
                  <p className="text-3xl font-bold text-gray-900">{flowData.total_flows}</p>
                </div>
                <div className="bg-white border rounded-xl p-4 text-center">
                  <p className="text-sm text-gray-500">Passed</p>
                  <p className="text-3xl font-bold text-emerald-600">{flowData.passed}</p>
                </div>
                <div className="bg-white border rounded-xl p-4 text-center">
                  <p className="text-sm text-gray-500">Failed</p>
                  <p className="text-3xl font-bold text-red-600">{flowData.failed}</p>
                </div>
              </div>
              <div className="bg-white border rounded-xl overflow-hidden">
                <div className="px-5 py-4 border-b flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">Flow Results</h3>
                  <span className={`text-sm font-medium px-3 py-1 rounded-full ${flowData.failed === 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                    {flowData.pass_rate} pass rate
                  </span>
                </div>
                <div className="divide-y">
                  {(flowData.flows || []).map((flow, idx) => (
                    <div key={idx} className="px-5 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {flow.status === 'pass' ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
                        <span className="text-sm font-medium text-gray-800">{flow.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">Code: {flow.response_code}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${flow.status === 'pass' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                          {flow.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="bg-white border rounded-xl p-8 text-center text-gray-500">Click the Flow Check tab or Run Full QA to test all critical flows</div>
          )}
        </div>
      )}

      {/* ===== FRIENDS INTEGRITY TAB ===== */}
      {activeTab === 'friends' && (
        <div className="space-y-4" data-testid="opal-friends">
          {tabLoading.friends ? (
            <div className="flex items-center justify-center h-32 text-gray-500"><RefreshCw className="w-5 h-5 animate-spin mr-2" /> Checking friends data integrity...</div>
          ) : friendsData ? (
            <>
              <div className={`border rounded-xl p-5 ${friendsData.status === 'healthy' ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                <div className="flex items-center gap-3">
                  {friendsData.status === 'healthy' ? <CheckCircle className="w-6 h-6 text-emerald-600" /> : <AlertTriangle className="w-6 h-6 text-amber-600" />}
                  <div>
                    <h2 className="text-lg font-semibold capitalize">Friends Data: {friendsData.status}</h2>
                    <p className="text-sm text-gray-600">{friendsData.total_users} total users analyzed</p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white border rounded-xl p-4 text-center">
                  <p className="text-sm text-gray-500">Total Users</p>
                  <p className="text-2xl font-bold text-gray-900">{friendsData.total_users}</p>
                </div>
                <div className="bg-white border rounded-xl p-4 text-center">
                  <p className="text-sm text-gray-500">Count Mismatches</p>
                  <p className={`text-2xl font-bold ${friendsData.count_mismatches > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{friendsData.count_mismatches}</p>
                </div>
                <div className="bg-white border rounded-xl p-4 text-center">
                  <p className="text-sm text-gray-500">List Mismatches</p>
                  <p className={`text-2xl font-bold ${friendsData.friends_list_mismatches > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{friendsData.friends_list_mismatches}</p>
                </div>
                <div className="bg-white border rounded-xl p-4 text-center">
                  <p className="text-sm text-gray-500">Missing GEMS Friend</p>
                  <p className={`text-2xl font-bold ${friendsData.gems_not_friend > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{friendsData.gems_not_friend}</p>
                </div>
              </div>
              {friendsData.issues?.length > 0 && (
                <div className="bg-white border rounded-xl overflow-hidden">
                  <div className="px-5 py-4 border-b"><h3 className="font-semibold text-gray-900">Issues Found</h3></div>
                  <div className="divide-y">
                    {friendsData.issues.map((issue, idx) => {
                      const sev = SEVERITY_COLORS[issue.severity] || SEVERITY_COLORS.P2;
                      return (
                        <div key={idx} className={`px-5 py-4 ${sev.bg}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${sev.badge}`}>{issue.severity}</span>
                            <span className="font-medium text-gray-900">{issue.component}</span>
                          </div>
                          <p className="text-sm text-gray-700">{issue.error}</p>
                          <p className="text-sm text-gray-500 mt-1">Fix: {issue.recommendation}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white border rounded-xl p-8 text-center text-gray-500">Click Friends Integrity tab or Run Full QA to check data consistency</div>
          )}
        </div>
      )}

      {/* ===== PARITY TAB ===== */}
      {activeTab === 'parity' && (
        <div className="space-y-4" data-testid="opal-parity">
          {tabLoading.parity ? (
            <div className="flex items-center justify-center h-32 text-gray-500"><RefreshCw className="w-5 h-5 animate-spin mr-2" /> Analyzing platform parity...</div>
          ) : parityData ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white border rounded-xl p-4"><p className="text-sm text-gray-500">Total Features</p><p className="text-2xl font-bold text-gray-900">{parityData.summary?.total_features}</p></div>
                <div className="bg-white border rounded-xl p-4"><p className="text-sm text-gray-500">Endpoints Alive</p><p className="text-2xl font-bold text-emerald-600">{parityData.summary?.endpoints_alive}</p></div>
                <div className="bg-white border rounded-xl p-4"><p className="text-sm text-gray-500">Web / Mobile</p><p className="text-2xl font-bold text-blue-600">{parityData.summary?.web_feature_count} / {parityData.summary?.mobile_feature_count}</p></div>
                <div className="bg-white border rounded-xl p-4"><p className="text-sm text-gray-500">Parity Score</p><p className="text-2xl font-bold text-violet-600">{parityData.summary?.parity_score}</p></div>
              </div>
              <div className="bg-white border rounded-xl overflow-hidden">
                <div className="px-5 py-4 border-b"><h3 className="font-semibold text-gray-900">Feature Parity Matrix</h3></div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-gray-600 font-medium">Category</th>
                        <th className="px-4 py-3 text-left text-gray-600 font-medium">Feature</th>
                        <th className="px-4 py-3 text-center text-gray-600 font-medium">Web</th>
                        <th className="px-4 py-3 text-center text-gray-600 font-medium">Mobile</th>
                        <th className="px-4 py-3 text-center text-gray-600 font-medium">Admin</th>
                        <th className="px-4 py-3 text-center text-gray-600 font-medium">Endpoint</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {(parityData.features || []).map((f, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-gray-700 capitalize">{f.category}</td>
                          <td className="px-4 py-3 text-gray-900 font-medium">{f.feature.replace(/_/g, ' ')}</td>
                          <td className="px-4 py-3 text-center">{f.web ? <CheckCircle className="w-4 h-4 text-emerald-500 mx-auto" /> : <XCircle className="w-4 h-4 text-gray-300 mx-auto" />}</td>
                          <td className="px-4 py-3 text-center">{f.mobile ? <CheckCircle className="w-4 h-4 text-emerald-500 mx-auto" /> : <XCircle className="w-4 h-4 text-gray-300 mx-auto" />}</td>
                          <td className="px-4 py-3 text-center">{f.admin ? <CheckCircle className="w-4 h-4 text-emerald-500 mx-auto" /> : <XCircle className="w-4 h-4 text-gray-300 mx-auto" />}</td>
                          <td className="px-4 py-3 text-center">
                            {f.endpoint_alive ? <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Live</span> : <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Down</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {parityData.parity_issues?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                  <h3 className="font-semibold text-amber-800 mb-2 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Parity Issues ({parityData.parity_issues.length})</h3>
                  <ul className="space-y-1">{parityData.parity_issues.map((issue, idx) => (<li key={idx} className="text-sm text-amber-700">&#8226; {issue}</li>))}</ul>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white border rounded-xl p-8 text-center text-gray-500">Click the Parity Check tab to load the report</div>
          )}
        </div>
      )}

      {/* ===== REGRESSIONS TAB ===== */}
      {activeTab === 'regressions' && (
        <div className="space-y-4" data-testid="opal-regressions">
          {tabLoading.regressions ? (
            <div className="flex items-center justify-center h-32 text-gray-500"><RefreshCw className="w-5 h-5 animate-spin mr-2" /> Checking for regressions...</div>
          ) : regressionsData ? (
            <>
              <div className={`border rounded-xl p-5 ${regressionsData.status === 'stable' ? 'bg-emerald-50 border-emerald-200' : regressionsData.status === 'insufficient_data' ? 'bg-gray-50 border-gray-200' : 'bg-red-50 border-red-200'}`}>
                <div className="flex items-center gap-3">
                  {regressionsData.status === 'stable' ? <CheckCircle className="w-6 h-6 text-emerald-600" /> :
                   regressionsData.status === 'insufficient_data' ? <Clock className="w-6 h-6 text-gray-500" /> :
                   <XCircle className="w-6 h-6 text-red-600" />}
                  <div>
                    <h2 className="text-lg font-semibold capitalize">{regressionsData.status === 'stable' ? 'No Regressions Detected' : regressionsData.status === 'insufficient_data' ? 'Need More Data' : 'Regressions Detected'}</h2>
                    <p className="text-sm text-gray-600">{regressionsData.checks_compared} health checks compared. Baseline: {regressionsData.baseline_status || 'N/A'} &rarr; Current: {regressionsData.current_status || 'N/A'}</p>
                  </div>
                </div>
              </div>
              {regressionsData.regressions?.length > 0 && (
                <div className="bg-white border rounded-xl overflow-hidden">
                  <div className="px-5 py-4 border-b"><h3 className="font-semibold text-gray-900">Detected Regressions</h3></div>
                  <div className="divide-y">
                    {regressionsData.regressions.map((reg, idx) => (
                      <div key={idx} className="px-5 py-4 bg-red-50">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800">{reg.type}</span>
                          <span className="font-medium text-gray-900">{reg.endpoint}</span>
                        </div>
                        <p className="text-sm text-gray-700">{reg.detail}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white border rounded-xl p-8 text-center text-gray-500">Click Regressions tab or Run Full QA to detect regressions</div>
          )}
        </div>
      )}

      {/* ===== RECURRING ISSUES TAB ===== */}
      {activeTab === 'recurring' && (
        <div className="space-y-4" data-testid="opal-recurring">
          {tabLoading.recurring ? (
            <div className="flex items-center justify-center h-32 text-gray-500"><RefreshCw className="w-5 h-5 animate-spin mr-2" /> Analyzing recurring issues...</div>
          ) : recurringData ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white border rounded-xl p-4 text-center"><p className="text-sm text-gray-500">Alerts Analyzed (7d)</p><p className="text-2xl font-bold text-gray-900">{recurringData.total_alerts_analyzed}</p></div>
                <div className="bg-white border rounded-xl p-4 text-center"><p className="text-sm text-gray-500">Unique Issues</p><p className="text-2xl font-bold text-blue-600">{recurringData.unique_issues}</p></div>
                <div className="bg-white border rounded-xl p-4 text-center"><p className="text-sm text-gray-500">Recurring (2+ times)</p><p className={`text-2xl font-bold ${recurringData.recurring_issues?.length > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{recurringData.recurring_issues?.length || 0}</p></div>
              </div>
              {recurringData.recurring_issues?.length > 0 ? (
                <div className="bg-white border rounded-xl overflow-hidden">
                  <div className="px-5 py-4 border-b"><h3 className="font-semibold text-gray-900">Recurring Issues</h3></div>
                  <div className="divide-y">
                    {recurringData.recurring_issues.map((issue, idx) => {
                      const sev = SEVERITY_COLORS[issue.severity] || SEVERITY_COLORS.P2;
                      return (
                        <div key={idx} className="px-5 py-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${sev.badge}`}>{issue.severity}</span>
                              <span className="font-medium text-gray-900">{issue.description}</span>
                            </div>
                            <span className="text-sm font-bold text-red-600">{issue.count}x</span>
                          </div>
                          <div className="text-sm text-gray-600 space-y-1">
                            {issue.where && <p>Where: {issue.where}</p>}
                            {issue.likely_cause && <p>Cause: {issue.likely_cause}</p>}
                            <p className="text-xs text-gray-400">First: {new Date(issue.first_seen).toLocaleString()} | Last: {new Date(issue.last_seen).toLocaleString()}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                  <p className="text-emerald-700">No recurring issues detected in the last 7 days.</p>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white border rounded-xl p-8 text-center text-gray-500">Click Recurring Issues tab or Run Full QA to analyze issue patterns</div>
          )}
        </div>
      )}

      {/* ===== TRENDS TAB ===== */}
      {activeTab === 'trends' && (
        <div className="space-y-4" data-testid="opal-trends">
          {tabLoading.trends ? (
            <div className="flex items-center justify-center h-32 text-gray-500"><RefreshCw className="w-5 h-5 animate-spin mr-2" /> Analyzing trends...</div>
          ) : trendsData ? (
            <>
              <div className={`border rounded-xl p-5 ${trendsData.trend === 'stable' ? 'bg-emerald-50 border-emerald-200' : trendsData.trend === 'degrading' ? 'bg-amber-50 border-amber-200' : trendsData.trend === 'critical' ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center gap-3">
                  {trendsData.trend === 'stable' ? <CheckCircle className="w-6 h-6 text-emerald-600" /> : trendsData.trend === 'degrading' ? <AlertTriangle className="w-6 h-6 text-amber-600" /> : trendsData.trend === 'critical' ? <XCircle className="w-6 h-6 text-red-600" /> : <Activity className="w-6 h-6 text-gray-500" />}
                  <div>
                    <h2 className="text-lg font-semibold capitalize">System Trend: {trendsData.trend || 'No Data'}</h2>
                    <p className="text-sm text-gray-600">{trendsData.checks_analyzed} checks over {trendsData.period_hours}h {trendsData.uptime_percentage !== undefined && `| ${trendsData.uptime_percentage}% uptime`}</p>
                  </div>
                </div>
              </div>
              {trendsData.status_breakdown && (
                <div className="grid grid-cols-3 gap-4">
                  {Object.entries(trendsData.status_breakdown).map(([s, count]) => (
                    <div key={s} className="bg-white border rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold" style={{ color: s === 'healthy' ? '#059669' : s === 'degraded' ? '#d97706' : '#dc2626' }}>{count}</p>
                      <p className="text-sm text-gray-500 capitalize mt-1">{s} Checks</p>
                    </div>
                  ))}
                </div>
              )}
              {trendsData.top_failing_endpoints?.length > 0 && (
                <div className="bg-white border rounded-xl overflow-hidden">
                  <div className="px-5 py-4 border-b"><h3 className="font-semibold text-gray-900">Top Failing Endpoints</h3></div>
                  <div className="divide-y">
                    {trendsData.top_failing_endpoints.map((ep, idx) => (
                      <div key={idx} className="px-5 py-3 flex items-center justify-between">
                        <span className="text-sm text-gray-800">{ep.name}</span>
                        <span className="text-sm font-medium text-red-600">{ep.failure_count} failures</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {trendsData.trend === 'no_data' && (
                <div className="bg-white border rounded-xl p-8 text-center text-gray-500">
                  <Clock className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p>No health check data available yet. Run a health check to start collecting trend data.</p>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white border rounded-xl p-8 text-center text-gray-500">Click the Trends tab to load analysis</div>
          )}
        </div>
      )}

      {/* ===== ALERTS TAB ===== */}
      {activeTab === 'alerts' && (
        <div className="space-y-4" data-testid="opal-alerts-list">
          {alerts.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
              <BellOff className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No active alerts</p>
            </div>
          ) : (
            alerts.map(alert => {
              const sev = SEVERITY_COLORS[alert.severity] || SEVERITY_COLORS.P2;
              return (
                <div key={alert.id} className={`${sev.bg} ${sev.border} border rounded-xl p-5`} data-testid={`opal-alert-${alert.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Bell className={`w-4 h-4 ${sev.text}`} />
                      <span className="font-semibold text-gray-900">{alert.title}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${sev.badge}`}>{alert.severity}</span>
                    </div>
                    <button onClick={() => dismissAlert(alert.id)} className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-white/50" data-testid={`dismiss-alert-${alert.id}`}>Dismiss</button>
                  </div>
                  <p className="text-sm text-gray-700 mb-3">{alert.summary}</p>
                  {alert.issues?.length > 0 && (
                    <div className="space-y-2">
                      {alert.issues.map((issue, idx) => (
                        <div key={idx} className="bg-white/70 rounded-lg p-3 text-sm">
                          <p className="font-medium text-gray-800">{issue.what}</p>
                          <p className="text-gray-600">Cause: {issue.likely_cause}</p>
                          <p className="text-gray-800">Fix: {issue.recommended_action}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-2 text-xs text-gray-500">
                    <Clock className="w-3 h-3 inline mr-1" />{new Date(alert.timestamp).toLocaleString()}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default OpalPage;
